import { useCallback, useEffect, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  Image as ImageIcon,
  Loader2,
  Presentation,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { platformFetch } from '../../../platform/http';
import toast from '../../Toast';
import { formatBeijingDate, formatBeijingDateTime } from '../../../utils/time';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';

interface PptImageRef {
  id: string;
  status: string;
  resultUrl: string | null;
  errorMessage: string | null;
  createdAt: string;
}

interface PptProjectSummary {
  id: string;
  title: string;
  topic: string | null;
  purpose: string | null;
  userId: string;
  userEmail: string;
  userName: string | null;
  pageCount: number;
  status: string;
  aspectRatio: string;
  stylePreset: string;
  generationQuality: string;
  completedSlideCount: number;
  failedSlideCount: number;
  backgroundStatus: string;
  backgroundImage: PptImageRef | null;
  createdAt: string;
  updatedAt: string;
}

interface PptSlideDetail {
  id: string;
  sortOrder: number;
  slideType: string;
  title: string | null;
  contentPrompt: string | null;
  imageJobId: string | null;
  generationStatus: string;
  referenceMode: string;
  createdAt: string;
  updatedAt: string;
  image: PptImageRef | null;
  variants: PptImageRef[];
}

interface PptProjectDetail {
  id: string;
  title: string;
  topic: string | null;
  purpose: string | null;
  userId: string;
  user: {
    id: string;
    email: string;
    name: string | null;
  };
  pageCount: number;
  baseModelId: string | null;
  inpaintModelId: string | null;
  generationQuality: string;
  aspectRatio: string;
  stylePreset: string;
  status: string;
  backgroundPrompt: string | null;
  backgroundCandidateIds: string[];
  backgroundImageJobId: string | null;
  backgroundImage: PptImageRef | null;
  backgroundCandidates: PptImageRef[];
  createdAt: string;
  updatedAt: string;
  slides: PptSlideDetail[];
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  background_pending: '背景生成中',
  background_ready: '背景就绪',
  generating: '页面生成中',
  completed: '已完成',
  idle: '未开始',
  queued: '排队中',
  processing: '处理中',
  failed: '失败',
  pending: '待处理',
};

const STATUS_BADGES: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700',
  background_pending: 'bg-amber-100 text-amber-700',
  background_ready: 'bg-sky-100 text-sky-700',
  generating: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  idle: 'bg-slate-100 text-slate-600',
  queued: 'bg-slate-100 text-slate-600',
  processing: 'bg-blue-100 text-blue-700',
  failed: 'bg-red-100 text-red-700',
  pending: 'bg-amber-100 text-amber-700',
};

function getStatusLabel(status: string) {
  return STATUS_LABELS[status] || status;
}

function getStatusBadge(status: string) {
  return STATUS_BADGES[status] || 'bg-slate-100 text-slate-700';
}

function formatDate(value: string) {
  return formatBeijingDateTime(value);
}

export default function PptProjectsTab() {
  const [projects, setProjects] = useState<PptProjectSummary[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [userEmailFilter, setUserEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [detailProject, setDetailProject] = useState<PptProjectDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchProjects = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (searchQuery) params.set('search', searchQuery);
      if (userEmailFilter) params.set('userEmail', userEmailFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const res = await platformFetch(`/admin/ppt/projects?${params.toString()}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || '获取 PPT 项目失败');
      }

      setProjects(data.data || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
    } catch (error) {
      console.error('获取 PPT 项目失败:', error);
      toast.error(error instanceof Error ? error.message : '获取 PPT 项目失败');
    } finally {
      setLoading(false);
    }
  }, [endDate, searchQuery, startDate, statusFilter, userEmailFilter]);

  useEffect(() => {
    fetchProjects(1);
  }, [fetchProjects]);

  const handleSearch = () => {
    fetchProjects(1);
  };

  const handleReset = () => {
    setSearchQuery('');
    setUserEmailFilter('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
  };

  const handleViewDetail = async (id: string) => {
    setDetailLoading(true);
    setDetailProject(null);
    try {
      const res = await platformFetch(`/admin/ppt/projects/${id}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || '获取 PPT 项目详情失败');
      }

      setDetailProject(data.data || null);
    } catch (error) {
      console.error('获取 PPT 项目详情失败:', error);
      toast.error(error instanceof Error ? error.message : '获取 PPT 项目详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1 min-w-0">
            <label className="text-sm text-light-muted mb-1 block">项目搜索</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-muted" />
              <input
                type="text"
                placeholder="搜索标题 / 主题 / 用途"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20"
              />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <label className="text-sm text-light-muted mb-1 block">用户邮箱</label>
            <input
              type="text"
              placeholder="按邮箱筛选"
              value={userEmailFilter}
              onChange={(e) => setUserEmailFilter(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20"
            />
          </div>
          <div className="w-full lg:w-44">
            <label className="text-sm text-light-muted mb-1 block">项目状态</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20"
            >
              <option value="">全部状态</option>
              <option value="draft">草稿</option>
              <option value="background_pending">背景生成中</option>
              <option value="background_ready">背景就绪</option>
              <option value="generating">页面生成中</option>
              <option value="completed">已完成</option>
            </select>
          </div>
          <div className="w-full lg:w-40">
            <label className="text-sm text-light-muted mb-1 block">开始日期</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20"
            />
          </div>
          <div className="w-full lg:w-40">
            <label className="text-sm text-light-muted mb-1 block">结束日期</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-light-primary text-white rounded-xl hover:bg-light-primary/90 transition-colors"
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">筛选</span>
            </button>
            <button
              onClick={handleReset}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-light-hover text-light-text rounded-xl hover:bg-light-border transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">重置</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-light-border bg-light-hover">
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">项目</th>
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">用户</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">页数</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">项目状态</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">背景状态</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">页面进度</th>
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">更新时间</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" />
                  </td>
                </tr>
              ) : projects.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-light-muted">
                    <Presentation className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>暂无 PPT 项目</p>
                  </td>
                </tr>
              ) : (
                projects.map((project) => (
                  <tr key={project.id} className="border-b border-light-border last:border-0 hover:bg-light-hover/50">
                    <td className="py-3 px-4 min-w-[240px]">
                      <p className="text-sm text-light-text font-medium">{project.title || '未命名 PPT'}</p>
                      <p className="text-xs text-light-muted mt-1">
                        {project.aspectRatio} / {project.stylePreset} / {project.generationQuality}
                      </p>
                      {project.topic && <p className="text-xs text-light-muted mt-1 line-clamp-1">{project.topic}</p>}
                    </td>
                    <td className="py-3 px-4 min-w-[180px]">
                      <p className="text-sm text-light-text">{project.userEmail}</p>
                      {project.userName && <p className="text-xs text-light-muted">{project.userName}</p>}
                    </td>
                    <td className="py-3 px-4 text-center text-sm text-light-text">{project.pageCount}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs ${getStatusBadge(project.status)}`}>
                        {getStatusLabel(project.status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs ${getStatusBadge(project.backgroundStatus)}`}>
                        {getStatusLabel(project.backgroundStatus)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="text-sm text-light-text">{project.completedSlideCount}/{project.pageCount}</div>
                      {project.failedSlideCount > 0 && (
                        <div className="text-xs text-red-500 mt-1">失败 {project.failedSlideCount}</div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-light-muted">{formatDate(project.updatedAt)}</td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => handleViewDetail(project.id)}
                        className="inline-flex items-center justify-center p-2 hover:bg-light-hover rounded-lg transition-colors"
                        title="查看详情"
                      >
                        <Eye className="w-4 h-4 text-light-muted" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="lg:hidden divide-y divide-light-border">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" />
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12 text-light-muted">
              <Presentation className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>暂无 PPT 项目</p>
            </div>
          ) : (
            projects.map((project) => (
              <div key={project.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-light-text">{project.title || '未命名 PPT'}</p>
                    <p className="text-xs text-light-muted mt-1">{project.userEmail}</p>
                  </div>
                  <button
                    onClick={() => handleViewDetail(project.id)}
                    className="p-1.5 hover:bg-light-hover rounded-lg"
                    title="查看详情"
                  >
                    <Eye className="w-4 h-4 text-light-muted" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs ${getStatusBadge(project.status)}`}>
                    {getStatusLabel(project.status)}
                  </span>
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs ${getStatusBadge(project.backgroundStatus)}`}>
                    背景 {getStatusLabel(project.backgroundStatus)}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-light-hover rounded-lg p-2 text-center">
                    <p className="text-xs text-light-muted">页数</p>
                    <p className="text-sm font-medium text-light-text">{project.pageCount}</p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-2 text-center">
                    <p className="text-xs text-light-muted">完成</p>
                    <p className="text-sm font-medium text-light-text">{project.completedSlideCount}</p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-2 text-center">
                    <p className="text-xs text-light-muted">更新</p>
                    <p className="text-xs text-light-text">{formatBeijingDate(project.updatedAt)}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 lg:px-6 py-4 border-t border-light-border gap-3">
            <p className="text-sm text-light-muted">
              共 {pagination.total} 条，第 {pagination.page} / {pagination.totalPages} 页
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchProjects(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-light-border hover:bg-light-hover text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">上一页</span>
              </button>
              <button
                onClick={() => fetchProjects(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-light-border hover:bg-light-hover text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="hidden sm:inline">下一页</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {(detailLoading || detailProject) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-light-border">
              <div>
                <h3 className="text-lg font-semibold text-light-text">PPT 项目详情</h3>
                <p className="text-sm text-light-muted mt-1">{detailProject?.title || '加载中...'}</p>
              </div>
              <button
                onClick={() => {
                  setDetailProject(null);
                  setDetailLoading(false);
                }}
                className="p-2 hover:bg-light-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-light-muted" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 lg:p-6">
              {detailLoading || !detailProject ? (
                <div className="text-center py-10">
                  <Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                    <div className="xl:col-span-2 bg-light-hover rounded-xl p-4">
                      <h4 className="text-sm font-medium text-light-muted mb-3">基本信息</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-light-muted">用户</p>
                          <p className="text-light-text">{detailProject.user.email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-light-muted">项目状态</p>
                          <p className="text-light-text">{getStatusLabel(detailProject.status)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-light-muted">主题</p>
                          <p className="text-light-text">{detailProject.topic || '未填写'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-light-muted">用途</p>
                          <p className="text-light-text">{detailProject.purpose || '未填写'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-light-muted">页数 / 比例</p>
                          <p className="text-light-text">{detailProject.pageCount} 页 / {detailProject.aspectRatio}</p>
                        </div>
                        <div>
                          <p className="text-xs text-light-muted">风格 / 质量</p>
                          <p className="text-light-text">{detailProject.stylePreset} / {detailProject.generationQuality}</p>
                        </div>
                        <div>
                          <p className="text-xs text-light-muted">基础模型</p>
                          <p className="text-light-text">{detailProject.baseModelId || '未配置'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-light-muted">重绘模型</p>
                          <p className="text-light-text">{detailProject.inpaintModelId || '未配置'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-light-muted">创建时间</p>
                          <p className="text-light-text">{formatDate(detailProject.createdAt)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-light-muted">更新时间</p>
                          <p className="text-light-text">{formatDate(detailProject.updatedAt)}</p>
                        </div>
                      </div>
                      {detailProject.backgroundPrompt && (
                        <div className="mt-4">
                          <p className="text-xs text-light-muted mb-1">背景提示词</p>
                          <p className="text-sm text-light-text whitespace-pre-wrap">{detailProject.backgroundPrompt}</p>
                        </div>
                      )}
                    </div>

                    <div className="bg-light-hover rounded-xl p-4">
                      <h4 className="text-sm font-medium text-light-muted mb-3">项目背景</h4>
                      {detailProject.backgroundImage?.resultUrl ? (
                        <img
                          src={resolveBrowserMediaUrl(detailProject.backgroundImage.resultUrl)}
                          alt="PPT 背景"
                          className="w-full aspect-[16/9] object-cover rounded-xl border border-light-border"
                        />
                      ) : (
                        <div className="w-full aspect-[16/9] rounded-xl border border-dashed border-light-border flex items-center justify-center text-light-muted text-sm bg-white">
                          暂无背景图
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs ${getStatusBadge(detailProject.backgroundImage?.status || 'idle')}`}>
                          {getStatusLabel(detailProject.backgroundImage?.status || 'idle')}
                        </span>
                        <span className="inline-flex rounded-full px-2.5 py-1 text-xs bg-slate-100 text-slate-700">
                          候选 {detailProject.backgroundCandidates.length}
                        </span>
                      </div>
                    </div>
                  </div>

                  {detailProject.backgroundCandidates.length > 0 && (
                    <div className="bg-light-hover rounded-xl p-4">
                      <h4 className="text-sm font-medium text-light-muted mb-3">背景候选图</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
                        {detailProject.backgroundCandidates.map((candidate) => (
                          <div key={candidate.id} className="bg-white rounded-xl p-2 border border-light-border">
                            {candidate.resultUrl ? (
                              <img
                                src={resolveBrowserMediaUrl(candidate.resultUrl)}
                                alt={candidate.id}
                                className="w-full aspect-[4/3] object-cover rounded-lg"
                              />
                            ) : (
                              <div className="w-full aspect-[4/3] rounded-lg bg-light-hover flex items-center justify-center text-light-muted">
                                <ImageIcon className="w-5 h-5" />
                              </div>
                            )}
                            <div className="mt-2">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ${getStatusBadge(candidate.status)}`}>
                                {getStatusLabel(candidate.status)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-light-hover rounded-xl p-4">
                    <h4 className="text-sm font-medium text-light-muted mb-3">
                      页面列表 ({detailProject.slides.length})
                    </h4>
                    <div className="space-y-3">
                      {detailProject.slides.map((slide) => (
                        <div key={slide.id} className="bg-white rounded-xl border border-light-border p-4">
                          <div className="flex flex-col lg:flex-row gap-4">
                            <div className="w-full lg:w-56 shrink-0">
                              {slide.image?.resultUrl ? (
                                <img
                                  src={resolveBrowserMediaUrl(slide.image.resultUrl)}
                                  alt={slide.title || `第 ${slide.sortOrder + 1} 页`}
                                  className="w-full aspect-[16/9] object-cover rounded-xl border border-light-border"
                                />
                              ) : (
                                <div className="w-full aspect-[16/9] rounded-xl border border-dashed border-light-border flex items-center justify-center text-light-muted text-sm bg-light-hover">
                                  暂无页面图
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="text-sm font-medium text-light-text">
                                  第 {slide.sortOrder + 1} 页 · {slide.title || '未命名页面'}
                                </span>
                                <span className="inline-flex rounded-full px-2.5 py-1 text-xs bg-slate-100 text-slate-700">
                                  {slide.slideType}
                                </span>
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs ${getStatusBadge(slide.generationStatus)}`}>
                                  {getStatusLabel(slide.generationStatus)}
                                </span>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="text-xs text-light-muted">参考模式</p>
                                  <p className="text-light-text">{slide.referenceMode}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-light-muted">候选版本</p>
                                  <p className="text-light-text">{slide.variants.length}</p>
                                </div>
                              </div>

                              {slide.contentPrompt && (
                                <div className="mt-3">
                                  <p className="text-xs text-light-muted mb-1">页面提示词</p>
                                  <p className="text-sm text-light-text whitespace-pre-wrap">{slide.contentPrompt}</p>
                                </div>
                              )}

                              {slide.variants.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-xs text-light-muted mb-2">候选版本</p>
                                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                                    {slide.variants.map((variant) => (
                                      <div key={variant.id} className="rounded-lg overflow-hidden border border-light-border bg-light-hover">
                                        {variant.resultUrl ? (
                                          <img
                                            src={resolveBrowserMediaUrl(variant.resultUrl)}
                                            alt={variant.id}
                                            className="w-full aspect-[4/3] object-cover"
                                          />
                                        ) : (
                                          <div className="w-full aspect-[4/3] flex items-center justify-center text-light-muted">
                                            <ImageIcon className="w-4 h-4" />
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
